import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  CalendarOutlined,
  ClockCircleOutlined,
  CopyOutlined,
  LinkOutlined,
  ReloadOutlined,
  UserOutlined,
} from '@ant-design/icons';
import {
  Button,
  Card,
  Col,
  Descriptions,
  Empty,
  message,
  Modal,
  Row,
  Space,
  Spin,
  Tag,
  Typography,
} from 'antd';
import { useContext, useEffect, useState } from 'react';
import ChatBubble from '../../components/ChatBubble';
import { Agent } from '../../types/index';
import { UserContext } from '../../UserContext';
import { formatDateFromUTCtoAgent, getAgentTimezone } from './utils';
import { escapeHTML, sanitizeHTML } from '../../utils';

const { Link, Text, Title } = Typography;

interface PromptTask {
  text?: string;
  title?: string;
  description?: string;
  due_datetime?: string;
  quoted_source_text?: string[];
}

interface PromptEmailDraft {
  body?: string;
  subject?: string;
}

interface PromptResult {
  tasks?: PromptTask[];
  summary?: string;
  email_draft?: PromptEmailDraft;
  appointments?: unknown[];
  note_heading?: string;
  bullet_summary?: string[];
  detailed_notes?: string;
  appointment_outcome?: string;
}

interface MeetingRecordingItem {
  id: string;
  created_at: string;
  transcription?: string;
  twilio_recording_url?: string;
  twilio_call_sid?: string;
  duration?: number;
  appointment_id?: string;
  prompt_result?: PromptResult;
  lead?: {
    id?: string;
    source_id?: string;
    first_name?: string;
    last_name?: string;
    email?: string;
    phone?: string;
  } | null;
  agent?: {
    id?: string;
    first_name?: string;
    last_name?: string;
  } | null;
}

const MeetingRecordingsList = ({
  agent,
  apiUrl,
  clientConfig,
}: {
  agent: Agent;
  apiUrl: string;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  clientConfig: any;
}) => {
  const user = useContext(UserContext);
  const [apiResponse, setApiResponse] = useState<MeetingRecordingItem[]>([]);
  const agentTimezone = getAgentTimezone(agent, clientConfig);
  const [loading, setLoading] = useState(false);
  const [sortAsc, setSortAsc] = useState(false);
  const [selectedRecording, setSelectedRecording] =
    useState<MeetingRecordingItem | null>(null);
  const [audioSrc, setAudioSrc] = useState<string | null>(null);

  useEffect(() => {
    if (!selectedRecording?.id || !user?.accessToken) {
      setAudioSrc(null);
      return;
    }

    let active = true;
    let objectUrl: string | null = null;

    const fetchAudio = async () => {
      try {
        const response = await fetch(
          `${apiUrl}/api/meeting-recordings-audio/${selectedRecording.id}`,
          {
            headers: {
              Authorization: `Bearer ${user.accessToken}`,
            },
          },
        );
        if (response.ok && active) {
          const blob = await response.blob();
          objectUrl = URL.createObjectURL(blob);
          setAudioSrc(objectUrl);
        }
      } catch (err) {
        // eslint-disable-next-line no-console
        console.error('Error fetching audio:', err);
      }
    };

    void fetchAudio();

    return () => {
      active = false;
      if (objectUrl) {
        URL.revokeObjectURL(objectUrl);
      }
      setAudioSrc(null);
    };
  }, [selectedRecording?.id, apiUrl, user?.accessToken]);

  const fetchMeetingRecordings = async () => {
    try {
      setLoading(true);
      if (!user?.accessToken) {
        setLoading(false);
        return;
      }

      const url = `${apiUrl}/api/meeting-recordings/agent/${agent.id}`;
      const response = await fetch(url, {
        headers: {
          Authorization: `Bearer ${user.accessToken}`,
        },
      });

      if (!response.ok) {
        throw new Error(
          `Failed to fetch meeting recordings: ${response.statusText}`,
        );
      }

      const jsonData = await response.json();
      setApiResponse(jsonData);
      setLoading(false);
    } catch (err) {
      // eslint-disable-next-line no-console
      console.error(
        'An error occurred while fetching the meeting recordings:',
        err,
      );
      setLoading(false);
    }
  };

  useEffect(() => {
    void fetchMeetingRecordings();
  }, [agent.id, apiUrl, user?.accessToken]);

  const openModal = (recording: MeetingRecordingItem) => {
    setSelectedRecording(recording);
  };

  const closeModal = () => {
    setSelectedRecording(null);
  };

  const formatDuration = (seconds?: number): string => {
    if (!seconds) {
      return 'N/A';
    }
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  const copyTranscript = async (transcription: string) => {
    try {
      await navigator.clipboard.writeText(transcription);
      void message.success('Transcript copied to clipboard!');
    } catch (err) {
      void message.error('Failed to copy transcript');
    }
  };

  return (
    <Card bordered>
      <Row justify="end" style={{ marginBottom: 12 }}>
        <Col>
          <Button
            icon={<ReloadOutlined />}
            loading={loading}
            onClick={() => {
              void fetchMeetingRecordings();
            }}
            type="default"
          >
            Refresh
          </Button>
        </Col>
        <Col>
          <Button
            icon={sortAsc ? <ArrowDownOutlined /> : <ArrowUpOutlined />}
            onClick={() => setSortAsc(prev => !prev)}
            style={{ marginLeft: 8 }}
            type="default"
          >
            Sort {sortAsc ? 'Descending' : 'Ascending'}
          </Button>
        </Col>
      </Row>
      <Spin spinning={loading}>
        <Space direction="vertical" size="middle" style={{ width: '100%' }}>
          {apiResponse.length === 0 ? (
            <Empty description="No meeting recordings found" />
          ) : (
            [...apiResponse]
              .sort((a, b) =>
                sortAsc
                  ? new Date(a.created_at).getTime() -
                    new Date(b.created_at).getTime()
                  : new Date(b.created_at).getTime() -
                    new Date(a.created_at).getTime(),
              )
              .map((recording, index) => {
                const transcription =
                  recording.transcription || 'No transcription available';
                const lead = recording.lead;
                const leadName = lead
                  ? `${lead.first_name || ''} ${lead.last_name || ''}`.trim()
                  : 'Unknown Lead';

                return (
                  <Row justify="start" key={recording.id || index}>
                    <Col>
                      <div
                        onClick={() => openModal(recording)}
                        onKeyPress={e => {
                          if (e.key === 'Enter' || e.key === ' ') {
                            openModal(recording);
                          }
                        }}
                        role="button"
                        style={{
                          cursor: 'pointer',
                          outline: 'none',
                          textAlign: 'left',
                        }}
                        tabIndex={0}
                      >
                        <div style={{ marginBottom: 4 }}>
                          <Space>
                            <Tag color="purple" icon={<CalendarOutlined />}>
                              Meeting Recording
                            </Tag>
                            {lead && <Tag color="blue">{leadName}</Tag>}
                            {recording.duration && (
                              <Tag color="default">
                                {formatDuration(recording.duration)}
                              </Tag>
                            )}
                          </Space>
                        </div>
                        <ChatBubble
                          isOwnMessage={false}
                          message={`<strong>${escapeHTML(transcription.substring(0, 200))}${transcription.length > 200 ? '...' : ''}</strong>${
                            recording.appointment_id
                              ? `<br /><span style="font-size: 12px; color: rgba(0, 0, 0, 0.45); display: block; margin-top: 4px;">Appointment ID: ${escapeHTML(recording.appointment_id)}</span>`
                              : ''
                          }`}
                          timestamp={formatDateFromUTCtoAgent(
                            recording.created_at,
                            agentTimezone,
                          )}
                        />
                      </div>
                    </Col>
                  </Row>
                );
              })
          )}
        </Space>
      </Spin>

      {/* Meeting Recording Detail Modal */}
      <Modal
        footer={null}
        onCancel={closeModal}
        open={!!selectedRecording}
        title={
          selectedRecording ? (
            <Space>
              <CalendarOutlined />
              Meeting Recording Details
            </Space>
          ) : null
        }
        width={900}
      >
        {selectedRecording && (
          <Space direction="vertical" size="large" style={{ width: '100%' }}>
            {/* Quick Actions */}
            <Row gutter={16}>
              <Col>
                <Link
                  href={`/rec/${selectedRecording.id}`}
                  rel="noopener noreferrer"
                  target="_blank"
                >
                  <Button icon={<LinkOutlined />} type="primary">
                    Open Full Recording Page
                  </Button>
                </Link>
              </Col>
              {selectedRecording.transcription && (
                <Col>
                  <Button
                    icon={<CopyOutlined />}
                    onClick={() =>
                      void copyTranscript(selectedRecording.transcription || '')
                    }
                  >
                    Copy Transcript
                  </Button>
                </Col>
              )}
            </Row>

            {/* Recording Information */}
            <Card size="small" title="Recording Information">
              <Descriptions column={2} size="small">
                <Descriptions.Item label="Recording ID">
                  {selectedRecording.id}
                </Descriptions.Item>
                <Descriptions.Item label="Created At">
                  {formatDateFromUTCtoAgent(
                    selectedRecording.created_at,
                    agentTimezone,
                  )}
                </Descriptions.Item>
                <Descriptions.Item label="Duration">
                  <Tag color="blue" icon={<ClockCircleOutlined />}>
                    {formatDuration(selectedRecording.duration)}
                  </Tag>
                </Descriptions.Item>
                <Descriptions.Item label="Appointment ID">
                  {selectedRecording.appointment_id || 'N/A'}
                </Descriptions.Item>
                {selectedRecording.twilio_call_sid && (
                  <Descriptions.Item label="Call SID">
                    {selectedRecording.twilio_call_sid}
                  </Descriptions.Item>
                )}
              </Descriptions>
            </Card>

            {/* Lead Information */}
            {selectedRecording.lead && (
              <Card size="small" title="Lead Information">
                <Descriptions column={2} size="small">
                  <Descriptions.Item label="Name">
                    <Tag color="blue" icon={<UserOutlined />}>
                      {selectedRecording.lead.first_name}{' '}
                      {selectedRecording.lead.last_name}
                    </Tag>
                  </Descriptions.Item>
                  <Descriptions.Item label="Lead ID">
                    {selectedRecording.lead.id || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Source ID">
                    {selectedRecording.lead.source_id || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Email">
                    {selectedRecording.lead.email || 'N/A'}
                  </Descriptions.Item>
                  <Descriptions.Item label="Phone">
                    {selectedRecording.lead.phone || 'N/A'}
                  </Descriptions.Item>
                </Descriptions>
              </Card>
            )}

            {/* Prompt Results - Summary & Analysis */}
            {selectedRecording.prompt_result && (
              <>
                {/* Summary */}
                {selectedRecording.prompt_result.summary && (
                  <Card size="small" title="Summary">
                    <Text>{selectedRecording.prompt_result.summary}</Text>
                  </Card>
                )}

                {/* Bullet Summary */}
                {selectedRecording.prompt_result.bullet_summary &&
                  selectedRecording.prompt_result.bullet_summary.length > 0 && (
                    <Card size="small" title="Key Points">
                      <ul style={{ margin: 0, paddingLeft: 20 }}>
                        {selectedRecording.prompt_result.bullet_summary.map(
                          (point, idx) => (
                            <li key={idx}>
                              <Text>{point}</Text>
                            </li>
                          ),
                        )}
                      </ul>
                    </Card>
                  )}

                {/* Appointment Outcome */}
                {selectedRecording.prompt_result.appointment_outcome && (
                  <Card size="small" title="Appointment Outcome">
                    <Tag color="green">
                      {selectedRecording.prompt_result.appointment_outcome}
                    </Tag>
                  </Card>
                )}

                {/* Tasks */}
                {selectedRecording.prompt_result.tasks &&
                  selectedRecording.prompt_result.tasks.length > 0 && (
                    <Card size="small" title="Generated Tasks">
                      <Space
                        direction="vertical"
                        size="small"
                        style={{ width: '100%' }}
                      >
                        {selectedRecording.prompt_result.tasks.map(
                          (task, idx) => (
                            <Card
                              key={idx}
                              size="small"
                              style={{ background: '#fafafa' }}
                            >
                              <Space direction="vertical" size={4}>
                                <Text strong>{task.title}</Text>
                                {task.description && (
                                  <Text type="secondary">
                                    {task.description}
                                  </Text>
                                )}
                                {task.due_datetime && (
                                  <Tag
                                    color="orange"
                                    icon={<CalendarOutlined />}
                                  >
                                    Due: {task.due_datetime}
                                  </Tag>
                                )}
                              </Space>
                            </Card>
                          ),
                        )}
                      </Space>
                    </Card>
                  )}

                {/* Email Draft */}
                {selectedRecording.prompt_result.email_draft && (
                  <Card size="small" title="Email Draft">
                    <Space
                      direction="vertical"
                      size="small"
                      style={{ width: '100%' }}
                    >
                      {selectedRecording.prompt_result.email_draft.subject && (
                        <div>
                          <Text strong>Subject: </Text>
                          <Text>
                            {
                              selectedRecording.prompt_result.email_draft
                                .subject
                            }
                          </Text>
                        </div>
                      )}
                      {selectedRecording.prompt_result.email_draft.body && (
                        <div
                          dangerouslySetInnerHTML={{
                            __html: sanitizeHTML(
                              selectedRecording.prompt_result.email_draft.body,
                            ),
                          }}
                          style={{
                            background: '#f5f5f5',
                            borderRadius: 8,
                            padding: 12,
                          }}
                        />
                      )}
                    </Space>
                  </Card>
                )}

                {/* Detailed Notes */}
                {selectedRecording.prompt_result.detailed_notes && (
                  <Card
                    size="small"
                    title={
                      selectedRecording.prompt_result.note_heading ||
                      'Detailed Notes'
                    }
                  >
                    <div
                      dangerouslySetInnerHTML={{
                        __html: sanitizeHTML(
                          selectedRecording.prompt_result.detailed_notes,
                        ),
                      }}
                      style={{
                        background: '#f5f5f5',
                        borderRadius: 8,
                        maxHeight: 300,
                        overflow: 'auto',
                        padding: 16,
                      }}
                    />
                  </Card>
                )}
              </>
            )}

            {/* Transcription */}
            <Card
              size="small"
              title={
                <Row align="middle" justify="space-between">
                  <Col>
                    <Title level={5} style={{ margin: 0 }}>
                      Transcription
                    </Title>
                  </Col>
                </Row>
              }
            >
              <div
                style={{
                  background: '#f5f5f5',
                  borderRadius: 8,
                  maxHeight: 400,
                  overflow: 'auto',
                  padding: 16,
                }}
              >
                {selectedRecording.transcription ? (
                  selectedRecording.transcription
                    .split('\n')
                    .map((line, idx) => (
                      <div key={idx} style={{ marginBottom: 8 }}>
                        <Text>{line}</Text>
                      </div>
                    ))
                ) : (
                  <Text>No transcription available.</Text>
                )}
              </div>
            </Card>

            {/* Audio Player */}
            {selectedRecording.twilio_recording_url && (
              <Card size="small" title="Recording Audio">
                {audioSrc ? (
                  <audio controls src={audioSrc} style={{ width: '100%' }} />
                ) : (
                  <div style={{ textAlign: 'center', padding: '10px' }}>
                    <Spin />
                  </div>
                )}
              </Card>
            )}
          </Space>
        )}
      </Modal>
    </Card>
  );
};

export default MeetingRecordingsList;
